import { type HierarchicalLogger } from '@atlassian/logger-interface';
import { type Metrics } from '@atlassian/metrics-interface';
import { type CachedTapClient } from '@post-office/cache-clients/clients/tap-client';
import { getTapTraitsForSiteUser, TapProvider } from '@post-office/util-tap-traits';

export type HasViewedModalParams = {
	/** Atlassian account ID of the recipient. May be missing (e.g. in local test servers). */
	userId: string | undefined;
	tenantId: string;
	/** TAP "first time" screen-viewed trait for the modal, e.g. `jira_screen_viewed_..._first_time`. */
	traitName: string;
	metrics: Metrics;
	logger: HierarchicalLogger;
	/** May be missing (e.g. in local test servers). */
	cachedTapClient: CachedTapClient | undefined;
};

/**
 * Returns whether the user has already viewed the recent downgrade discount modal, based on its
 * TAP "first time" screen-viewed trait.
 *
 * Never throws: this only decides whether the modal is forced open, so a missing user or a TAP
 * failure must not drop the whole message. In those cases it returns `true` ("viewed") so the
 * modal is not forced open when we can't confirm the user hasn't seen it, rather than risk
 * popping it up on every visit.
 */
export const hasViewedModal = async ({
	userId,
	tenantId,
	traitName,
	metrics,
	logger,
	cachedTapClient,
}: HasViewedModalParams): Promise<boolean> => {
	if (!userId || !cachedTapClient) {
		logger.warn(
			{ traitName, hasUserId: !!userId, hasTapClient: !!cachedTapClient },
			'recent-downgrade-discount: missing user or TAP client, treating modal as viewed',
		);
		return true;
	}

	try {
		const siteUserTapTraits = await getTapTraitsForSiteUser({
			userId,
			siteId: tenantId,
			tapProvider: new TapProvider(metrics, logger, cachedTapClient),
			logger,
		});

		return Boolean(siteUserTapTraits?.[traitName]);
	} catch (error) {
		logger.warn(
			{ error, traitName },
			'recent-downgrade-discount: failed to fetch TAP traits, treating modal as viewed',
		);
		return true;
	}
};
